import React, { useState, useRef, useMemo, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import { Box, Typography, IconButton, Menu, MenuItem, CircularProgress, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { EChartsOption, RadarSeriesOption, SeriesOption } from 'echarts';
import { exportChartToPng } from '../../utils/chartUtils';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';

// Type definitions
export interface RadarChartDataItem {
  attribute: string;
  [key: string]: string | number;
}

export interface RadarSeries {
  dataKey: string;
  name?: string;
  color?: string;
  fillOpacity?: number;
}

// Add support for chart.js-like data format
export interface ChartJsRadarData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
    borderColor: string;
  }[];
}

export interface RadarSeriesAlternative {
  name: string;
  value: number[];
  color?: string;
}

export interface ERadarChartProps {
  /** Chart data - can be either RadarChartDataItem[] or ChartJsRadarData */
  data?: RadarChartDataItem[] | ChartJsRadarData;
  /** Data series to display */
  series?: RadarSeries[] | RadarSeriesAlternative[];
  /** Chart title */
  title?: string;
  /** Chart height */
  height?: number;
  /** Format tooltips */
  tooltipFormatter?: (value: number | string) => string;
  /** Max value for radar chart */
  maxValue?: number;
  /** Data is refreshing status */
  isRefreshing?: boolean;
  /** Last updated timestamp */
  lastUpdated?: Date;
  /** Function to refresh data */
  onRefresh?: () => void;
  /** Indicators for radar chart when using alternative data format */
  indicators?: Array<{name: string; max: number}>;
}

/**
 * Radar chart component implemented with ECharts
 * @param props - Component properties
 * @returns React component
 */
const ERadarChart: React.FC<ERadarChartProps> = ({
  data: inputData,
  series: inputSeries,
  indicators,
  title,
  height = 400,
  tooltipFormatter,
  maxValue,
  isRefreshing = false,
  onRefresh,
  lastUpdated: propLastUpdated
}) => {
  const chartRef = useRef<ReactECharts>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(propLastUpdated || new Date());
  
  // Process data for ECharts format with proper dependency tracking
  const { indicators: chartIndicators, seriesData } = useMemo(() => {
    // Case 1: If both indicators and series are provided directly in the right format for echarts
    if (!inputData && indicators && Array.isArray(inputSeries)) {
      const isDirectRadarSeries = inputSeries.length > 0 && 'value' in inputSeries[0];
      
      if (isDirectRadarSeries) {
        // This is already in the expected format for ECharts
        const seriesWithTypes = (inputSeries as RadarSeriesAlternative[]).map(series => ({
          ...series,
          type: 'radar' as const,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: { width: 2 },
          areaStyle: {
            opacity: 0.6,
            color: series.color
          },
          itemStyle: {
            color: series.color
          }
        }));
        
        return { indicators, seriesData: seriesWithTypes };
      }
    }
    
    // Case 2: Handle Chart.js-like data format
    if (inputData && !Array.isArray(inputData) && 'labels' in inputData && 'datasets' in inputData) {
      const chartIndicators = inputData.labels.map(label => ({ 
        name: label,
        max: maxValue || Math.max(...inputData.datasets.flatMap(ds => ds.data)) * 1.2
      }));
      
      const series = inputData.datasets.map(dataset => ({
        name: dataset.label,
        value: dataset.data,
        type: 'radar' as const,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 2
        },
        areaStyle: {
          opacity: 0.6,
          color: dataset.backgroundColor
        },
        itemStyle: {
          color: dataset.borderColor
        }
      }));
      
      return { indicators: chartIndicators, seriesData: series };
    }
    
    // Case 3: Handle standard data format
    const data = inputData as RadarChartDataItem[] || [];
    
    // Get all attributes for the radar indicators
    const chartIndicators = data.map(item => ({ 
      name: item.attribute, 
      max: maxValue || Math.max(...Object.values(item).filter(
        val => typeof val === 'number'
      ) as number[]) * 1.2
    }));
    
    // Handle when inputSeries is standard RadarSeries[]
    if (inputSeries && inputSeries.length > 0 && 'dataKey' in inputSeries[0]) {
      const seriesList = inputSeries as RadarSeries[];
      
      // Convert to ECharts series format
      const series = seriesList.map(serie => {
        // Extract values for this series from each data item
        const values = chartIndicators.map(indicator => {
          const dataItem = data.find(item => item.attribute === indicator.name);
          return dataItem ? (dataItem[serie.dataKey] as number) || 0 : 0;
        });

        return {
          name: serie.name || serie.dataKey,
          value: values,
          type: 'radar' as const,
          symbol: 'circle',
          symbolSize: 6,
          lineStyle: {
            width: 2
          },
          areaStyle: {
            opacity: serie.fillOpacity || 0.6,
            color: serie.color
          },
          itemStyle: {
            color: serie.color
          }
        };
      });

      return { indicators: chartIndicators, seriesData: series };
    }
    
    // Auto-generate series if none provided
    if (data.length > 0) {
      const firstItem = data[0];
      const generatedSeries = Object.keys(firstItem)
        .filter(key => key !== 'attribute')
        .map(key => {
          // Extract values for this series from each data item
          const values = chartIndicators.map(indicator => {
            const dataItem = data.find(item => item.attribute === indicator.name);
            return dataItem ? (dataItem[key] as number) || 0 : 0;
          });

          return {
            name: key,
            value: values,
            type: 'radar' as const,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              width: 2
            },
            areaStyle: {
              opacity: 0.6
            }
          };
        });

      return { indicators: chartIndicators, seriesData: generatedSeries };
    }

    return { indicators: [], seriesData: [] };
  }, [inputData, inputSeries, maxValue, indicators]);

  // Create ECharts options with proper dependencies
  const options: EChartsOption = useMemo(() => ({
    title: title ? {
      text: title,
      left: 'center'
    } : undefined,
    tooltip: {
      trigger: 'item',
      formatter: (params: any) => {
        if (tooltipFormatter && typeof params.value === 'number') {
          return tooltipFormatter(params.value);
        }
        
        if (Array.isArray(params.value)) {
          // Format each value with the attribute name
          return `<strong>${params.seriesName}</strong><br/>` + 
            (chartIndicators || []).map((indicator, i) => 
              `${indicator.name}: ${params.value[i]}`
            ).join('<br/>');
        }
        
        return '';
      }
    },
    legend: {
      data: seriesData.map(s => s.name),
      bottom: 0
    },    radar: {
      indicator: chartIndicators || indicators,
      shape: 'polygon',
      splitNumber: 5,
      axisName: {
        color: '#333',
        fontSize: 12
      }
    },
    series: seriesData as SeriesOption[]
  }), [title, tooltipFormatter, seriesData, chartIndicators, indicators]);

  // Create debounced refresh handler for performance
  const handleRefresh = useCallback(debounce(() => {
    if (onRefresh) {
      onRefresh();
      setLastUpdated(new Date());
    }
  }, 300), [onRefresh]);

  // Create export handlers with useCallback to prevent unnecessary re-renders
  const handleExportMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  }, []);
  
  const handleExportMenuClose = useCallback(() => {
    setExportMenuAnchor(null);
  }, []);
  
  const handleExportPng = useCallback(() => {
    handleExportMenuClose();
    if (chartRef.current) {
      const echartInstance = chartRef.current.getEchartsInstance();
      const dataURL = echartInstance.getDataURL();
      const link = document.createElement('a');
      link.download = `${title || 'radar-chart'}.png`;
      link.href = dataURL;
      link.click();
    }
  }, [title, handleExportMenuClose]);

  const formattedLastUpdated = lastUpdated 
    ? `Updated: ${lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'})}`
    : '';
  // Handle empty data
  const isEmpty = Array.isArray(inputData) ? inputData.length === 0 : false;
  
  return (
    <Box sx={{ width: '100%', mb: 3, position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        {title && <Typography variant="h6" sx={{ fontWeight: 'medium' }}>{title}</Typography>}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastUpdated && (
            <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', mr: 2 }}>
              {formattedLastUpdated}
            </Box>
          )}
          
          {onRefresh && (
            <Tooltip title="Refresh data">
              <IconButton size="small" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          <Tooltip title="Export options">
            <IconButton size="small" onClick={handleExportMenuOpen}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>          <Menu
            anchorEl={exportMenuAnchor}
            open={exportMenuOpen}
            onClose={handleExportMenuClose}
          >
            <MenuItem onClick={handleExportPng}>Export as PNG</MenuItem>
            <MenuItem onClick={handleExportMenuClose}>Export as PDF</MenuItem>
            <MenuItem onClick={handleExportMenuClose}>Export data as CSV</MenuItem>
          </Menu>
        </Box>
      </Box>
        <Box
        sx={{
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          position: 'relative'        }}
      >
        {isEmpty && (
          <Box 
            sx={{ 
              height: height,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary'
            }}
          >
            <Typography variant="body1">No data available</Typography>
          </Box>
        )}
        
        {isRefreshing && (
          <Box 
            sx={{ 
              position: 'absolute', 
              top: 0, 
              left: 0, 
              right: 0, 
              bottom: 0, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              background: 'rgba(255, 255, 255, 0.7)', 
              borderRadius: 1, 
              zIndex: 5 
            }}
          >
            <CircularProgress size={30} />
          </Box>
        )}
          {!isEmpty && (
          <ReactECharts
            ref={chartRef}
            option={options}
            style={{ height: height }}
            opts={{ renderer: 'canvas' }}
            notMerge={true}
          />
        )}
      </Box>
    </Box>
  );
};

// Export with React.memo for performance optimization
export default React.memo(ERadarChart);
